import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { Close, OpenInNew } from "@mui/icons-material";
import { SmartInlineText } from "./SmartInlineText";
import { stripTrailingNewsEllipsis } from "../utils/newsFormat";
import { normalizeHttpUrl } from "../utils/allianceQuickLinks";

export interface NewsArticlePreview {
  title?: string;
  timestamp?: string;
  lead?: string;
  content?: string;
  link?: string;
}

interface NewsPreviewDialogProps {
  open: boolean;
  article: NewsArticlePreview | null;
  onClose: () => void;
}

export function NewsPreviewDialog({
  open,
  article,
  onClose,
}: NewsPreviewDialogProps) {
  const a = article;
  const titleRaw = a && a.title != null ? String(a.title) : "";
  const title =
    stripTrailingNewsEllipsis(titleRaw) ||
    (a?.link ? String(a.link) : "Vijest");

  const lead = a?.lead ? stripTrailingNewsEllipsis(String(a.lead)) : "";
  const content = a?.content != null ? String(a.content).trim() : "";

  const hasOriginal = Boolean(a?.link);

  return (
    <Dialog
      open={open && !!a}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      aria-labelledby="news-preview-title"
      sx={{
        "& .MuiDialog-paper": {
          maxWidth: "min(calc(100vw - 48px), 728px)",
        },
      }}
    >
      <DialogTitle
        id="news-preview-title"
        sx={{
          pr: 6,
          fontSize: "1.15rem",
          fontWeight: 600,
          lineHeight: 1.35,
        }}
      >
        {title}
        <IconButton
          aria-label="Zatvori"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {a?.timestamp && (
          <Typography
            variant="caption"
            color="secondary.main"
            sx={{ display: "block", mb: 1.5 }}
          >
            {a.timestamp}
          </Typography>
        )}
        {lead && (
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ mb: content ? 2 : 0, fontStyle: "italic" }}
          >
            <SmartInlineText text={lead} />
          </Typography>
        )}
        {content && (
          <Typography
            variant="body1"
            component="div"
            sx={{
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              "& a": { wordBreak: "break-all" },
            }}
          >
            <SmartInlineText text={content} />
          </Typography>
        )}
        {!lead && !content && (
          <Typography variant="body2" color="text.secondary">
            Nema dostupnog teksta za pregled. Otvorite izvorni članak.
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "flex-end" }}>
        <Button onClick={onClose} color="inherit">
          Zatvori
        </Button>
        {hasOriginal && (
          <Button
            component="a"
            href={normalizeHttpUrl(String(a?.link))}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            endIcon={<OpenInNew sx={{ fontSize: "1.1rem !important" }} />}
          >
            Otvori izvorni članak
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
